<template>
  <div class="modal fade" id="companyBillingClosingModal" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Fechamento de cobrança - {{ company?.company_name }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <div v-if="!company" class="text-muted">Nenhuma empresa selecionada.</div>
          <template v-else>
            <ul class="nav nav-tabs nav-line-tabs mb-4">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'fechamento' }" href="#" @click.prevent="activeTab = 'fechamento'">
                  <i class="bi bi-calendar-plus me-2"></i>Fechamento
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'historico' }" href="#" @click.prevent="switchToHistorico">
                  <i class="bi bi-clock-history me-2"></i>Histórico
                </a>
              </li>
            </ul>

            <!-- Tab Fechamento -->
            <div v-show="activeTab === 'fechamento'">
              <div class="row mb-4">
                <div class="col-md-12 mb-3" :hidden="activeContracts.length === 1">
                  <label class="form-label fw-bold">Selecione o contrato</label>
                  <DropdownUbbi
                    v-if="activeContracts.length > 1"
                    v-model="selectedContractId"
                    :options="contractOptions"
                    optionLabel="label"
                    optionValue="id_contract"
                    placeholder="Selecione o contrato"
                    :filter="true"
                    appendTo="self"
                    class="w-100"
                  />
                  <div v-else-if="activeContracts.length === 1" class="form-control form-control-solid py-2 bg-light">
                    {{ contractOptions[0]?.label || (activeContracts[0]?.contract_number || 'Contrato ativo') }}
                  </div>
                  <p v-else-if="activeContracts.length === 0 && !contractsLoading" class="text-muted small mb-0">
                    Nenhum contrato ativo encontrado para esta empresa.
                  </p>
                  <p v-else-if="contractsLoading" class="text-muted small mb-0">
                    <span class="spinner-border spinner-border-sm me-1"></span> Carregando contratos...
                  </p>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Mês/Referência (MM/AAAA)</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="reference"
                    placeholder="Ex: 02/2026"
                    maxlength="7"
                    @input="formatReference"
                  />
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <button type="button" class="btn btn-primary" @click="loadPreview" :disabled="loading || !referenceValid || !selectedContractId">
                    <span v-if="loadingPreview" class="spinner-border spinner-border-sm me-2"></span>
                    Carregar
                  </button>
                </div>
              </div>

              <div v-if="previewError" class="alert alert-danger">{{ previewError }}</div>

              <template v-if="preview && !existingClosing">
                <div class="table-responsive mb-3">
                  <table class="table table-sm table-hover">
                    <thead>
                    <tr class="fw-bold text-muted">
                      <th style="width: 50px;">Incluir</th>
                      <th>Funcionário</th>
                      <th>Dependentes (pagantes)</th>
                      <th class="text-end">Valor</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in preview.subscribers" :key="item.id_subscriber">
                      <td>
                        <input
                          type="checkbox"
                          class="form-check-input"
                          :value="item.id_subscriber"
                          v-model="includedIds"
                        />
                      </td>
                      <td>{{ item.subscriber_name }}</td>
                      <td>{{ item.paid_dependents }}</td>
                      <td class="text-end">R$ {{ formatMoney(item.value) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                  <div class="d-flex flex-wrap gap-4 align-items-center">
                    <span class="text-muted">Funcionários: <strong class="text-dark">{{ previewSummary.employees }}</strong></span>
                    <span class="text-muted">Dependentes: <strong class="text-dark">{{ previewSummary.dependents }}</strong></span>
                    <span class="text-muted">Total (pessoas): <strong class="text-dark">{{ previewSummary.total }}</strong></span>
                  </div>
                  <div>
                    <strong>Total cobrança:</strong>
                    <strong class="fs-5 ms-2">R$ {{ formatMoney(preview.total) }}</strong>
                  </div>
                </div>
                <p class="text-muted small">
                  Desmarcar um funcionário remove-o do fechamento e cancela a assinatura dele nesta empresa.
                </p>
              </template>
            </div>

            <!-- Tab Histórico -->
            <div v-show="activeTab === 'historico'">
              <div v-if="loadingClosings" class="text-center py-4">
                <span class="spinner-border spinner-border-sm me-2"></span> Carregando histórico...
              </div>
              <div v-else-if="closings.length === 0" class="text-muted text-center py-4">
                Nenhum fechamento encontrado.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm table-hover table-row-bordered">
                  <thead>
                  <tr class="fw-bold text-muted">
                    <th style="width: 44px;"></th>
                    <th>Referência</th>
                    <th class="text-end">Valor</th>
                    <th>Data fechamento</th>
                    <th>Usuário</th>
                    <th style="width: 190px;" class="text-end">Ações</th>
                  </tr>
                  </thead>
                  <tbody>
                  <template v-for="closing in closings" :key="closing.id">
                    <tr>
                      <td class="align-middle">
                        <button
                          type="button"
                          class="btn btn-sm btn-icon btn-light btn-active-light-primary p-0 border-0"
                          :aria-expanded="expandedClosingId === closing.id"
                          @click="toggleClosingExpand(closing.id)"
                          title="Ver funcionários"
                        >
                          <i class="bi fs-5" :class="expandedClosingId === closing.id ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                        </button>
                      </td>
                      <td class="align-middle">{{ closing.charge_reference }}</td>
                      <td class="align-middle text-end">R$ {{ formatMoney(closing.charge_amount) }}</td>
                      <td class="align-middle">{{ formatDate(closing.closed_at) }}</td>
                      <td class="align-middle">{{ closing.user_closed?.user_name || '—' }}</td>
                      <td class="align-middle text-end">
                        <button
                          type="button"
                          class="btn btn-sm btn-light-danger me-2"
                          @click="downloadClosingReportPdf(closing.id, closing.charge_reference)"
                        >
                          <i class="bi bi-file-pdf"></i>
                          PDF
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-light-success"
                          @click="downloadClosingReportExcel(closing.id, closing.charge_reference)"
                        >
                          <i class="bi bi-file-earmark-excel"></i>
                          Excel
                        </button>
                      </td>
                    </tr>
                    <tr v-if="expandedClosingId === closing.id" class="bg-light">
                      <td colspan="6" class="p-0 border-0">
                        <div class="p-3 ps-5">
                          <div v-if="loadingClosingReportId === closing.id" class="text-muted">
                            <span class="spinner-border spinner-border-sm me-2"></span> Carregando relatório...
                          </div>
                          <template v-else-if="closingReports[closing.id]">
                            <div class="d-flex flex-wrap gap-4 align-items-center mb-3">
                              <span class="text-muted">Funcionários: <strong class="text-dark">{{ closingReports[closing.id].summary.employees }}</strong></span>
                              <span class="text-muted">Dependentes: <strong class="text-dark">{{ closingReports[closing.id].summary.dependents }}</strong></span>
                              <span class="text-muted">Dependentes cobrados: <strong class="text-dark">{{ closingReports[closing.id].summary.paid_dependents }}</strong></span>
                              <span class="text-muted">Total: <strong class="text-dark">R$ {{ closingReports[closing.id].closing.charge_amount }}</strong></span>
                            </div>
                            <table class="table table-sm table-bordered mb-0 bg-white">
                              <thead>
                              <tr class="table-light">
                                <th>Funcionário</th>
                                <th>CPF</th>
                                <th class="text-end">Número dependentes</th>
                                <th class="text-end">Valor</th>
                              </tr>
                              </thead>
                              <tbody>
                              <template v-for="sub in closingReports[closing.id].subscribers" :key="sub.id_subscriber">
                              <tr>
                                <td>
                                  <div class="fw-semibold">{{ sub.subscriber_name }}</div>
                                </td>
                                <td>{{ sub.subscriber_cpf || '—' }}</td>
                                <td class="text-end">{{ sub.dependents_count }}</td>
                                <td class="text-end">R$ {{ sub.total_value }}</td>
                              </tr>
                              <tr v-for="dep in sub.dependents" :key="dep.id_dependent + '-dep'">
                                <td colspan="2" class="ps-6 text-muted">
                                  &gt; {{ dep.name }}<span v-if="dep.relationship"> ({{ dep.relationship }})</span>
                                </td>
                                <td class="text-end text-muted">—</td>
                                <td class="text-end text-muted">—</td>
                              </tr>
                              </template>
                              <tr v-if="closingReports[closing.id].subscribers.length === 0">
                                <td colspan="4" class="text-muted">—</td>
                              </tr>
                              </tbody>
                            </table>
                          </template>
                        </div>
                      </td>
                    </tr>
                  </template>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Cancelar</button>
          <button
            v-if="activeTab === 'fechamento' && preview && !existingClosing"
            type="button"
            class="btn btn-primary"
            @click="submitClose"
            :disabled="loading || includedIds.length === 0"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            Fechar cobrança
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from "@/helpers/modal";
import Alert from "@/helpers/alert";
import Loader from "@/helpers/loader";
import http from "@/helpers/http";
import moment from "moment";
import DropdownUbbi from "@/components/DropdownUbbi.vue";
import { CompanyService } from "@/service/companyService";

export default {
  name: "CompanyBillingClosingModal",
  components: { DropdownUbbi },
  props: ['company'],
  emits: ['onClose', 'closed'],
  data() {
    return {
      activeTab: 'fechamento',
      reference: '',
      loading: false,
      loadingPreview: false,
      preview: null,
      previewError: null,
      existingClosing: false,
      includedIds: [],
      placeId: null,
      closings: [],
      loadingClosings: false,
      expandedClosingId: null,
      closingReports: {},
      loadingClosingReportId: null,
      activeContracts: [],
      selectedContractId: null,
      contractsLoading: false,
      companyService: CompanyService.new(),
    };
  },
  computed: {
    referenceValid() {
      return /^\d{1,2}\/\d{4}$/.test(this.reference);
    },
    contractOptions() {
      return this.activeContracts.map((c) => ({
        id_contract: c.id_contract,
        label: `${c.contract_number || 'Contrato #' + c.id_contract}${c.template?.template_name ? ' - ' + c.template.template_name : ''}`,
      }));
    },
    previewSummary() {
      if (!this.preview || !this.preview.subscribers || !this.preview.subscribers.length) {
        return { employees: 0, dependents: 0, total: 0 };
      }
      const included = this.preview.subscribers.filter((s) => this.includedIds.includes(s.id_subscriber));
      const employees = included.length;
      const dependents = included.reduce((acc, s) => acc + (s.dependents_count ?? 0), 0);
      return {
        employees,
        dependents,
        total: employees + dependents,
      };
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(v) {
        if (v && this.$store.state.auth.place) {
          this.placeId = this.$store.state.auth.place.id_place;
          this.loadActiveContracts();
        } else {
          this.activeContracts = [];
          this.selectedContractId = null;
        }
        this.reset();
      },
    },
  },
  methods: {
    formatReference() {
      let v = this.reference.replace(/\D/g, '');
      if (v.length >= 2) {
        v = v.slice(0, 2) + '/' + v.slice(2, 6);
      }
      this.reference = v;
    },
    reset() {
      this.preview = null;
      this.previewError = null;
      this.existingClosing = false;
      this.includedIds = [];
      this.closings = [];
      this.expandedClosingId = null;
      this.closingReports = {};
      this.loadingClosingReportId = null;
    },
    loadActiveContracts() {
      if (!this.company || !this.placeId) return;
      this.contractsLoading = true;
      this.companyService.contracts(this.placeId, this.company.id_company, { per_page: 100 })
        .then((response) => {
          const list = response.data?.data ?? response.data ?? [];
          const all = Array.isArray(list) ? list : [];
          this.activeContracts = all.filter((c) => c.contract_status === 'active');
          this.selectedContractId = this.activeContracts[0]?.id_contract ?? null;
        })
        .catch(() => {
          this.activeContracts = [];
          this.selectedContractId = null;
        })
        .finally(() => {
          this.contractsLoading = false;
        });
    },
    switchToHistorico() {
      this.activeTab = 'historico';
      if (this.closings.length === 0 && this.company && this.placeId) {
        this.loadClosings();
      }
    },
    async loadClosings() {
      if (!this.company || !this.placeId) return;
      this.loadingClosings = true;
      try {
        const { data } = await http.get(
          `/places/${this.placeId}/plans/companies/${this.company.id_company}/billing/closings`
        );
        this.closings = data.data || [];
      } catch (e) {
        this.closings = [];
      } finally {
        this.loadingClosings = false;
      }
    },
    async toggleClosingExpand(closingId) {
      this.expandedClosingId = this.expandedClosingId === closingId ? null : closingId;
      if (this.expandedClosingId && !this.closingReports[closingId]) {
        await this.loadClosingReport(closingId);
      }
    },
    async loadClosingReport(closingId) {
      if (!this.company || !this.placeId) return;
      this.loadingClosingReportId = closingId;
      try {
        const { data } = await this.companyService.billingClosingInvoice(this.placeId, this.company.id_company, closingId);
        this.closingReports = {
          ...this.closingReports,
          [closingId]: data.data,
        };
      } catch (_) {
        Alert.error('Erro ao carregar relatório da fatura.');
      } finally {
        this.loadingClosingReportId = null;
      }
    },
    async downloadClosingReportPdf(closingId, reference) {
      try {
        Loader.show();
        const response = await this.companyService.exportBillingClosingInvoicePdf(this.placeId, this.company.id_company, closingId);
        this.downloadBlob(response.data, `relatorio_fatura_${(reference || 'fechamento').replace('/', '_')}.pdf`);
      } catch (_) {
        Alert.error('Erro ao exportar PDF.');
      } finally {
        Loader.close();
      }
    },
    async downloadClosingReportExcel(closingId, reference) {
      try {
        Loader.show();
        const response = await this.companyService.exportBillingClosingInvoiceExcel(this.placeId, this.company.id_company, closingId);
        this.downloadBlob(response.data, `relatorio_fatura_${(reference || 'fechamento').replace('/', '_')}.xlsx`);
      } catch (_) {
        Alert.error('Erro ao exportar Excel.');
      } finally {
        Loader.close();
      }
    },
    downloadBlob(data, filename) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    subscriberDisplayName(sub) {
      if (sub.user && sub.user.user_name) return sub.user.user_name;
      const mainDep = (sub.dependents || []).find(d => d.dep_main);
      if (mainDep && mainDep.patient && mainDep.patient.patient_name) return mainDep.patient.patient_name;
      return '—';
    },
    formatDate(date) {
      if (!date) return '—';
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    async loadPreview() {
      if (!this.company || !this.placeId || !this.referenceValid || !this.selectedContractId) return;
      this.loadingPreview = true;
      this.previewError = null;
      this.existingClosing = false;
      try {
        const { data } = await http.get(
          `/places/${this.placeId}/plans/companies/${this.company.id_company}/billing/preview`,
          { params: { reference: this.reference, id_contract: this.selectedContractId } }
        );
        this.preview = data.data;
        this.includedIds = (this.preview.subscribers || []).map(s => s.id_subscriber);
      } catch (e) {
        const data = e.response?.data;
        const msg =
          (typeof data === 'string' ? data : null) ||
          data?.message ||
          data?.error ||
          (Array.isArray(data?.errors) ? data.errors.join(' ') : data?.errors) ||
          'Erro ao carregar preview.';
        if (e.response?.status === 422 || msg.includes('Já existe')) {
          this.existingClosing = true;
        }
        this.previewError = msg;
        this.preview = null;
      } finally {
        this.loadingPreview = false;
      }
    },
    async submitClose() {
      if (!this.company || !this.placeId || this.includedIds.length === 0) return;
      this.loading = true;
      try {
        const previewIds = (this.preview.subscribers || []).map(s => s.id_subscriber);
        const removedSubscriberIds = previewIds.filter(id => !this.includedIds.includes(id));
        await http.post(
          `/places/${this.placeId}/plans/companies/${this.company.id_company}/billing/close`,
          {
            id_contract: this.selectedContractId,
            reference: this.reference,
            subscriber_ids: this.includedIds,
            removed_subscriber_ids: removedSubscriberIds,
            new_employees: [],
          }
        );
        Alert.success('Fechamento realizado com sucesso.');
        this.$emit('closed');
        this.closeModal();
      } catch (e) {
        const data = e.response?.data;
        let rawMsg = null;
        const raw = e.response?.request?.responseText;
        if (typeof raw === 'string' && raw.length > 0 && raw.length < 500) {
          try {
            rawMsg = raw.startsWith('"') ? JSON.parse(raw) : raw;
          } catch (_) {
            rawMsg = raw;
          }
        }
        const msg =
          (typeof data === 'string' ? data : null) ||
          data?.message ||
          data?.error ||
          (Array.isArray(data?.errors) ? data.errors.join(' ') : data?.errors) ||
          rawMsg ||
          'Erro ao fechar cobrança.';
        Alert.error(msg);
      } finally {
        this.loading = false;
      }
    },
    formatMoney(value) {
      if (value == null) return '0,00';
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    closeModal() {
      this.reset();
      this.$emit('onClose');
      Modal.toggle('#companyBillingClosingModal');
    },
  },
};
</script>
